import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ToggleGroup, ToggleGroupCount, ToggleGroupItem } from "@/components/ui/toggle-group";
import { DataTable, Icon, KpiCard, KpiGrid, Tip } from "@/components/ds";
import {
  FormDrawer,
  FormDrawerContent,
  FormDrawerDescription,
  FormDrawerFooter,
  FormDrawerFooterHint,
  FormDrawerHeader,
  FormDrawerTitle,
  FormDrawerClose,
  formDrawerBodyClass,
} from "@/components/ds/FormDrawer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertCircle, Loader2, Plus, Save, Search } from "@/components/ds/icons";
import { PageHeader } from "@/components/ui/PageHeader";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { DriveStep } from "driver.js";
import { useGuide } from "@/hooks/useGuide";
import { useRegisterPageGuide } from "@/contexts/ActiveGuideContext";
import { useGuideAutostart } from "@/hooks/useGuideAutostart";
import { usePendingGuide } from "@/hooks/usePendingGuide";
import { useSetupStatusContext } from "@/contexts/SetupStatusContext";
import { esAreaGeneral } from "@/lib/area";

// ─── Vocabulario ─────────────────────────────────────────────────────────────

interface Area {
  id: number;
  nombre: string;
  slug: string;
  descripcion: string | null;
  eliminado_at?: string | null;
  personas_count: number;
  cargos_count: number;
  contratos_count: number;
  vehiculos_count: number;
  maquinarias_count: number;
}

/**
 * Las cuatro vistas del listado. `papelera` NO es un filtro más sobre las áreas
 * activas: es otro conjunto que se le pide al servidor (`?en_papelera=1`), por
 * eso viaja como vista y no como un valor del filtro.
 *
 * Antes de este rediseño la papelera no tenía ninguna entrada en la interfaz:
 * `DELETE /areas/{id}` es un soft-delete, así que el área desaparecía del
 * listado, seguía ocupando su slug —el 422 de la API dice literalmente "puede
 * estar en la papelera"— y no había forma de traerla de vuelta ni de purgarla.
 */
type Vista = "todas" | "sin_uso" | "sin_descripcion" | "papelera";

function plural(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

function normalizar(s: string): string {
  return s.trim().toLowerCase();
}

function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .normalize("NFD")
      // Marcas diacríticas combinantes (U+0300–U+036F): es lo que deja el NFD al
      // separar "á" en "a" + tilde. Escrito por code point a propósito — el rango
      // literal es invisible en el editor y se pierde en cualquier copiado.
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")
  );
}

/** Cuántos registros de la empresa apuntan a esta área. */
function usoTotal(a: Area): number {
  return (
    a.personas_count +
    a.cargos_count +
    a.contratos_count +
    a.vehiculos_count +
    a.maquinarias_count
  );
}

// ─── Celdas ──────────────────────────────────────────────────────────────────

function CeldaArea({ area }: { area: Area }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 pr-4">
      <span className="flex min-w-0 items-center gap-2">
        <span className="fc-cell fc-truncate">{area.nombre}</span>
        {esAreaGeneral(area) && (
          <Tip texto="El área que la empresa trae al crearse. Puedes renombrarla o describirla, pero no eliminarla.">
            {/* `shrink`, no `shrink-0`: dentro de una celda de la grilla el
                `shrink-0` del Badge gana sobre el `min-w-0` del contenedor y el
                chip desborda la columna. */}
            <Badge variant="navy" className="shrink">
              Por defecto
            </Badge>
          </Tip>
        )}
      </span>
      <span className={cn("fc-meta fc-truncate", !area.descripcion && "text-soft italic")}>
        {area.descripcion || "Sin descripción"}
      </span>
    </div>
  );
}

/**
 * Cuántos trabajadores tiene el área y qué parte del total representa.
 *
 * La barra es de REPARTO, no de cumplimiento: acá no hay un valor bueno y otro
 * malo —un área con el 80% de la gente no está ni mejor ni peor que una con el
 * 5%—, así que va en el tono de marca y nunca en verde/ámbar/rojo. Por eso
 * tampoco usa `ProgressBar`, que colorea por umbral. El porcentaje va escrito
 * al lado igual, como pide el Design System.
 */
function CeldaTrabajadores({
  area,
  totalAsignaciones,
}: {
  area: Area;
  totalAsignaciones: number;
}) {
  const n = area.personas_count;
  const pct = totalAsignaciones > 0 ? Math.round((n / totalAsignaciones) * 100) : 0;

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1 pr-4">
      <span className="flex items-baseline gap-1.5">
        <span className="fc-num text-[13px]">{n}</span>
        <span className="text-soft text-[11px]">{totalAsignaciones > 0 ? `${pct}%` : "—"}</span>
      </span>
      <span className="bg-border-subtle block h-1 overflow-hidden rounded-full">
        <span
          className={cn("block h-full rounded-full", n > 0 && "bg-primary")}
          style={{ width: `${pct}%` }}
        />
      </span>
    </div>
  );
}

function EstadoBadge({ area }: { area: Area }) {
  if (area.eliminado_at) {
    return (
      <Badge variant="muted" dot>
        En papelera
      </Badge>
    );
  }
  const enUso = usoTotal(area) > 0;
  return (
    <Badge variant={enUso ? "success" : "neutral"} dot>
      {enUso ? "En uso" : "Sin uso"}
    </Badge>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export function Areas() {
  const [activas, setActivas] = useState<Area[]>([]);
  const [papelera, setPapelera] = useState<Area[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [vista, setVista] = useState<Vista>("todas");
  const [search, setSearch] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editando, setEditando] = useState<Area | null>(null);

  // Mover a papelera (DELETE → soft delete)
  const [aEliminar, setAEliminar] = useState<Area | null>(null);
  const [eliminando, setEliminando] = useState(false);

  // Restaurar desde papelera
  const [aRestaurar, setARestaurar] = useState<Area | null>(null);
  const [restaurando, setRestaurando] = useState(false);

  // Purgar (hard delete)
  const [aPurgar, setAPurgar] = useState<Area | null>(null);
  const [purgando, setPurgando] = useState(false);
  const [confirmPurgarTexto, setConfirmPurgarTexto] = useState("");

  // --- Guía de Áreas ---
  const { invalidate } = useSetupStatusContext();

  /**
   * guideInDialog: true mientras la guía está activa con el diálogo abierto.
   * Se usa para pasar modal={false} al FormDrawer durante esos pasos,
   * evitando que el focus trap de Radix bloquee los botones Siguiente/Anterior
   * del popover de driver.js (que se renderizan fuera del DOM del diálogo).
   *
   * En el flujo normal (sin guía) el diálogo sigue siendo modal={true} por defecto.
   */
  const [guideInDialog, setGuideInDialog] = useState(false);

  /**
   * Ref para cancelar el rAF de polling del primer campo del diálogo.
   * Se necesita para no tener efectos secundarios si el componente desmonta.
   */
  const dialogPollRafRef = useRef<number>(0);

  /**
   * Fábrica de pasos lazy: se invoca en startGuide() para capturar el estado
   * vivo de setCreateOpen y setGuideInDialog (estables entre renders).
   *
   * Paso 2 — Highlight del botón Nueva área:
   *   - onHighlightStarted: agrega driver-fc-dialog-step al body → driver z-index
   *     baja a 40/45 (bajo Radix z-50), el botón queda resaltado sin tapar nada.
   *   - onDeselected: remueve driver-fc-dialog-step → driver vuelve a su z default (~10000).
   *   - onNextClick: abre el diálogo, activa modal={false} en el dialog, hace poll
   *     rAF para esperar que monte el primer campo antes de avanzar.
   *
   * Pasos 3–5 — Campos dentro del diálogo:
   *   - driver está en z default (~10000), por encima del Radix Dialog (z-50).
   *   - driver-fc-dialog-step NO está en el body → overlay de driver es visible y
   *     el cutout expone el campo seleccionado.
   *   - Radix Dialog tiene modal={false} → focus trap desactivado →
   *     botones Next/Prev de driver son clickeables.
   *
   * Paso 6 — Popover de cierre:
   *   - Desactiva modal={false}, el diálogo puede cerrarse normalmente.
   */
  const buildAreasGuideSteps = useCallback((): DriveStep[] => [
    // Paso 1 — Popover centrado de apertura (sin elemento anclado)
    {
      popover: {
        title: "¿Por qué crear áreas?",
        description:
          "Las áreas dividen tu empresa en frentes de trabajo — por ejemplo: Faena, Mantención o Administración. Clasificar así a trabajadores, cargos, contratos y equipos mejora los filtros y los reportes del sistema. Tu empresa ya cuenta con el área «General» por defecto; esta guía te acompaña a crear las que reflejan tu estructura real.",
      },
    },
    // Paso 2 — Highlight del botón Nueva área (pre-diálogo)
    // driver-fc-dialog-step body class está activa → driver z-index bajo (40/45) bajo Radix
    {
      element: '[data-tour="areas-crear"]',
      onHighlightStarted: () => {
        document.body.classList.add("driver-fc-dialog-step");
      },
      onDeselected: () => {
        // Removemos la clase antes de los pasos in-dialog para que driver
        // vuelva a su z default (~10000) y pueda resaltar los campos dentro del diálogo.
        document.body.classList.remove("driver-fc-dialog-step");
      },
      popover: {
        title: "Crear un área",
        description:
          "Desde aquí defines un área para tu empresa. Haz clic en «Siguiente» para abrir el formulario.",
        onNextClick: (_el, _step, { driver: driverObj }) => {
          // Abre el diálogo y desactiva el focus trap para que driver mantenga
          // el control. El poll rAF espera a que monte el primer campo antes
          // de avanzar, evitando que driver intente resaltar un nodo inexistente.
          setCreateOpen(true);
          setGuideInDialog(true);

          cancelAnimationFrame(dialogPollRafRef.current);
          const deadline = performance.now() + 3000;
          const tick = () => {
            if (document.querySelector('[data-tour="area-nombre"]')) {
              driverObj.moveNext();
              return;
            }
            if (performance.now() >= deadline) {
              // Fallback: avanzamos igual para no dejar la guía atascada.
              driverObj.moveNext();
              return;
            }
            dialogPollRafRef.current = requestAnimationFrame(tick);
          };
          dialogPollRafRef.current = requestAnimationFrame(tick);
        },
      },
    },
    // Paso 3 — Campo Nombre (dentro del diálogo, primer campo visible)
    {
      element: '[data-tour="area-nombre"]',
      popover: {
        title: "Nombre del área",
        description:
          "Asigna un nombre claro al área, por ejemplo «Obras civiles» o «Administración». Este nombre aparecerá en filtros, reportes y asignaciones de trabajadores.",
      },
    },
    // Paso 4 — Campo Descripción (dentro del diálogo)
    {
      element: '[data-tour="area-descripcion"]',
      popover: {
        title: "Descripción del área",
        description:
          "Describe qué frente de trabajo cubre esta área, qué equipos o funciones incluye y cualquier nota relevante. Una buena descripción le dice al resto del equipo cuándo elegirla y mejora la calidad de los reportes de prevención.",
      },
    },
    // Paso 5 — Botón Guardar (dentro del diálogo)
    {
      element: '[data-tour="areas-guardar"]',
      popover: {
        title: "Guardar área",
        description: "Cuando termines de completar los datos, guarda para crear el área.",
        onNextClick: (_el, _step, { driver: driverObj }) => {
          // Al salir de los pasos in-dialog, volvemos al comportamiento modal normal.
          setGuideInDialog(false);
          driverObj.moveNext();
        },
      },
    },
    // Paso 6 — Popover de cierre/confirmación (sin elemento anclado)
    {
      popover: {
        title: "¡Listo!",
        description:
          "Puedes reabrir esta guía cuando quieras haciendo clic en «Ver guía» en el panel de primeros pasos, o en el botón de ayuda de esta página.",
      },
    },
  ], [setCreateOpen, setGuideInDialog]);

  const { startGuide } = useGuide("guide-areas", { buildSteps: buildAreasGuideSteps });
  useRegisterPageGuide(startGuide);
  usePendingGuide("guide-areas", startGuide, { anchorSelector: '[data-tour="areas-crear"]' });
  useGuideAutostart("guide-areas", startGuide, { anchorSelector: '[data-tour="areas-crear"]' });

  // Limpieza del rAF de polling al desmontar
  useEffect(() => {
    return () => cancelAnimationFrame(dialogPollRafRef.current);
  }, []);
  // --- fin Guía de Áreas ---

  /**
   * Dos pedidos, no uno por pestaña: activas y papelera son conjuntos distintos
   * del servidor, y el riel muestra el conteo de las cuatro vistas aunque el
   * usuario esté parado en una sola. Además el alta necesita los nombres de la
   * papelera para avisar del choque de slug ANTES de mandar el POST.
   */
  const cargar = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const [act, pap] = await Promise.all([
        api.get<{ data: Area[] }>("/areas?all=1"),
        api.get<{ data: Area[] }>("/areas?all=1&en_papelera=1"),
      ]);
      setActivas(act.data);
      setPapelera(pap.data);
    } catch {
      setLoadError("No se pudo cargar el listado de áreas.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  // ── Mover a papelera (DELETE → soft delete) ───────────────────────────────
  const moverAPapelera = async () => {
    if (!aEliminar) return;
    setEliminando(true);
    try {
      await api.delete(`/areas/${aEliminar.id}`);
      toast.success(
        `Área "${aEliminar.nombre}" movida a la papelera. Puedes restaurarla cuando quieras.`,
      );
      setAEliminar(null);
      cargar();
      invalidate();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        // El único guard del servidor: una empresa no puede quedarse sin áreas.
        toast.error(
          "Tu empresa debe tener al menos un área activa. Crea otra antes de mover esta a la papelera.",
        );
      } else {
        toast.error("No se pudo mover el área a la papelera.");
      }
    } finally {
      setEliminando(false);
    }
  };

  // ── Restaurar desde papelera ──────────────────────────────────────────────
  const restaurar = async () => {
    if (!aRestaurar) return;
    setRestaurando(true);
    try {
      await api.post(`/areas/${aRestaurar.id}/restaurar`, {});
      toast.success(`Área "${aRestaurar.nombre}" restaurada.`);
      setARestaurar(null);
      cargar();
      invalidate();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        toast.error(
          "Ya existe un área activa con ese nombre. Renómbrala antes de restaurar esta.",
        );
      } else {
        toast.error("No se pudo restaurar el área.");
      }
    } finally {
      setRestaurando(false);
    }
  };

  // ── Purgar (hard delete, irreversible) ────────────────────────────────────
  const purgar = async () => {
    if (!aPurgar) return;
    setPurgando(true);
    try {
      await api.delete(`/areas/${aPurgar.id}/purgar`);
      toast.success(`Área "${aPurgar.nombre}" eliminada permanentemente.`);
      setAPurgar(null);
      setConfirmPurgarTexto("");
      cargar();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) {
        toast.error("Solo se puede purgar un área desde la papelera.");
      } else {
        toast.error("No se pudo purgar el área.");
      }
      setAPurgar(null);
      setConfirmPurgarTexto("");
    } finally {
      setPurgando(false);
    }
  };

  // ── Conjuntos derivados ───────────────────────────────────────────────────

  const sinUso = useMemo(() => activas.filter((a) => usoTotal(a) === 0), [activas]);
  const sinDescripcion = useMemo(
    () => activas.filter((a) => !a.descripcion?.trim()),
    [activas],
  );

  /**
   * Ojo con el denominador: `personas_count` cuenta FILAS del pivote
   * `areas_personas`, y ese pivote es muchos-a-muchos. Un trabajador en dos
   * áreas suma dos. Por eso la tarjeta dice "asignaciones" y no "trabajadores":
   * llamarlo trabajadores daría un número que no se puede reconciliar con el
   * total del listado de Trabajadores.
   */
  const totalAsignaciones = useMemo(
    () => activas.reduce((acc, a) => acc + a.personas_count, 0),
    [activas],
  );

  const filas = useMemo(() => {
    const base =
      vista === "todas"
        ? activas
        : vista === "sin_uso"
          ? sinUso
          : vista === "sin_descripcion"
            ? sinDescripcion
            : papelera;

    const q = search.trim().toLowerCase();
    if (!q) return base;
    return base.filter(
      (a) =>
        a.nombre.toLowerCase().includes(q) ||
        a.slug.toLowerCase().includes(q) ||
        (a.descripcion ?? "").toLowerCase().includes(q),
    );
  }, [vista, activas, sinUso, sinDescripcion, papelera, search]);

  const esPapelera = vista === "papelera";

  const mensajeVacio = search.trim()
    ? "Ningún área coincide con esa búsqueda."
    : esPapelera
      ? "No hay áreas en la papelera."
      : vista === "sin_uso"
        ? "Todas tus áreas están en uso."
        : vista === "sin_descripcion"
          ? "Todas tus áreas tienen descripción."
          : 'Todavía no hay áreas. Haz clic en "Nueva área" para empezar.';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Áreas"
        subtitle="Divide la empresa en frentes de trabajo para filtrar y agrupar en terreno."
        rightContent={
          <Button data-tour="areas-crear" onClick={() => setCreateOpen(true)} className="shrink-0">
            <Plus className="h-4 w-4" />
            Nueva área
          </Button>
        }
      />

      {/*
        modal={false} desactiva el focus trap de Radix Dialog durante los pasos
        in-dialog de la guía. Esto permite que los botones Siguiente/Anterior de
        driver.js (renderizados fuera del DOM del diálogo) sean clickeables.
        En el flujo normal sin guía, guideInDialog=false y el diálogo es modal.
      */}
      <FormDrawer
        open={createOpen}
        onOpenChange={(open) => {
          if (!open) setGuideInDialog(false);
          setCreateOpen(open);
        }}
        side="right"
        modal={guideInDialog ? false : undefined}
        dismissible={guideInDialog ? false : undefined}
      >
        <AreaFormDrawer
          key={createOpen ? "create-open" : "create-closed"}
          mode="create"
          activas={activas}
          papelera={papelera}
          onSaved={() => {
            setCreateOpen(false);
            setGuideInDialog(false);
            cargar();
            invalidate();
          }}
        />
      </FormDrawer>

      <KpiGrid min={210}>
        <KpiCard
          label="Áreas definidas"
          valor={activas.length}
          detalle={`${activas.length - sinUso.length} en uso · ${sinUso.length} sin uso`}
        />
        <KpiCard
          label="Asignaciones a trabajadores"
          valor={totalAsignaciones}
          detalle={
            totalAsignaciones === 0
              ? "todavía no le asignaste un área a ningún trabajador"
              : "un trabajador puede pertenecer a más de un área"
          }
        />
        <KpiCard
          label="Áreas sin uso"
          valor={sinUso.length}
          detalle={`de ${plural(
            activas.length,
            "área definida",
            "áreas definidas",
          )}: sin trabajadores, cargos, contratos ni equipos`}
        />
      </KpiGrid>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-12 pt-6">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
          </CardContent>
        </Card>
      ) : loadError ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 pt-6 text-center">
            <AlertCircle className="text-destructive h-6 w-6" />
            <p className="text-muted-foreground text-sm">{loadError}</p>
            <button type="button" onClick={cargar} className="text-primary-hover text-sm underline">
              Reintentar
            </button>
          </CardContent>
        </Card>
      ) : (
        <DataTable
          rows={filas}
          getRowKey={(a) => a.id}
          // 586px de columnas + 28px del `px-3.5` de la fila. Por debajo de esa
          // suma la grilla se desborda del contenedor en vez de scrollear y la
          // cabecera deja de alinearse con las filas.
          minWidth={620}
          pageSize={20}
          onRowClick={esPapelera ? undefined : (a) => setEditando(a)}
          emptyMessage={mensajeVacio}
          emptyIcon={esPapelera ? "delete" : search.trim() ? "search_off" : "label"}
          toolbar={
            <>
              <ToggleGroup
                type="single"
                variant="segmented"
                value={vista}
                onValueChange={(v) => {
                  if (!v) return;
                  setVista(v as Vista);
                  setSearch("");
                }}
              >
                <ToggleGroupItem value="todas">
                  Todas
                  <ToggleGroupCount>{activas.length}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="sin_uso">
                  Sin uso
                  <ToggleGroupCount>{sinUso.length}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="sin_descripcion">
                  Sin descripción
                  <ToggleGroupCount>{sinDescripcion.length}</ToggleGroupCount>
                </ToggleGroupItem>
                <ToggleGroupItem value="papelera">
                  <Icon name="delete" size={16} />
                  Papelera
                  <ToggleGroupCount>{papelera.length}</ToggleGroupCount>
                </ToggleGroupItem>
              </ToggleGroup>

              <div className="relative min-w-[190px] flex-1">
                <Search className="text-soft absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2" />
                <Input
                  placeholder="Buscar por nombre o descripción…"
                  className="h-9 pl-8 text-[13px]"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </>
          }
          columns={[
            {
              key: "area",
              header: "Área",
              width: "minmax(260px,2.2fr)",
              cell: (a) => <CeldaArea area={a} />,
            },
            {
              key: "trabajadores",
              header: "Trabajadores",
              width: "150px",
              cell: (a) =>
                // En la papelera el reparto no aplica: el área salió del padrón
                // sobre el que se calcula el porcentaje.
                esPapelera ? (
                  <span className="fc-num text-soft text-[13px]">{a.personas_count}</span>
                ) : (
                  <CeldaTrabajadores area={a} totalAsignaciones={totalAsignaciones} />
                ),
            },
            {
              key: "estado",
              header: "Estado",
              width: "104px",
              cell: (a) => <EstadoBadge area={a} />,
            },
            {
              // Sin encabezado y con cadena vacía, NO `srOnlyHeader`: `sr-only`
              // es `position:absolute`, así que sobre una celda de la grilla la
              // saca del flujo y corre todas las columnas siguientes.
              key: "acciones",
              header: "",
              width: "72px",
              align: "right",
              cell: (a) =>
                esPapelera ? (
                  <div className="flex items-center justify-end gap-0.5">
                    <Tip texto="Devolver al listado">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Restaurar ${a.nombre}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setARestaurar(a);
                        }}
                      >
                        <Icon name="restore_from_trash" size={16} />
                      </Button>
                    </Tip>
                    <Tip texto="Eliminar definitivamente">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-soft hover:text-destructive"
                        aria-label={`Eliminar definitivamente ${a.nombre}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setAPurgar(a);
                          setConfirmPurgarTexto("");
                        }}
                      >
                        <Icon name="delete_forever" size={16} />
                      </Button>
                    </Tip>
                  </div>
                ) : (
                  <div className="flex items-center justify-end gap-0.5">
                    <Tip texto="Editar área">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Editar ${a.nombre}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditando(a);
                        }}
                      >
                        <Icon name="edit" size={16} />
                      </Button>
                    </Tip>
                    {/*
                      El área por defecto no se elimina: `ProvisionarEmpresaConOwner`
                      la busca por nombre para asignársela al dueño y es el destino de
                      quien todavía no tiene frente asignado. La API no lo impide —su
                      único guard es "no dejar a la empresa sin áreas"—, así que esta
                      regla es de interfaz y vive acá.
                    */}
                    {esAreaGeneral(a) ? (
                      <Tip texto="El área por defecto no se puede eliminar.">
                        <span className="flex size-7 items-center justify-center">
                          <Icon name="lock" size={15} className="text-icon-soft" />
                        </span>
                      </Tip>
                    ) : (
                      <Tip texto="Mover a la papelera">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-soft hover:text-destructive"
                          aria-label={`Mover ${a.nombre} a la papelera`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setAEliminar(a);
                          }}
                        >
                          {/* `delete`, no `delete_outline`: en Material Symbols el
                              relleno es un EJE (FILL), no un nombre distinto, y la
                              app carga la fuente con FILL@0 — así que `delete` ya es
                              la variante de contorno. */}
                          <Icon name="delete" size={16} />
                        </Button>
                      </Tip>
                    )}
                  </div>
                ),
            },
          ]}
        />
      )}

      <FormDrawer
        open={editando !== null}
        onOpenChange={(open) => !open && setEditando(null)}
        side="right"
      >
        {editando && (
          <AreaFormDrawer
            key={`edit-${editando.id}`}
            mode="edit"
            area={editando}
            activas={activas}
            papelera={papelera}
            onSaved={() => {
              setEditando(null);
              cargar();
            }}
          />
        )}
      </FormDrawer>

      {/* Confirmar: mover a papelera */}
      <AlertDialog open={aEliminar !== null} onOpenChange={(open) => !open && setAEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mover a papelera</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {aEliminar ? (
                  <div className="space-y-2">
                    <p>
                      ¿Quieres mover el área <strong>{aEliminar.nombre}</strong> a la papelera?
                    </p>
                    {usoTotal(aEliminar) > 0 ? (
                      <p>
                        Deja de aparecer en los filtros y en los formularios, pero{" "}
                        <strong>no se pierde ninguna asignación</strong>: los{" "}
                        {plural(aEliminar.personas_count, "trabajador", "trabajadores")}, cargos,
                        contratos y equipos que la tienen la recuperan intacta si la restauras.
                      </p>
                    ) : (
                      <p>No la está usando nadie, así que no afecta a ningún registro.</p>
                    )}
                    <p className="text-muted-foreground text-xs">
                      Mientras esté en la papelera su identificador sigue reservado: no vas a poder
                      crear otra área con el mismo nombre hasta restaurarla o eliminarla
                      definitivamente.
                    </p>
                  </div>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={eliminando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                moverAPapelera();
              }}
              disabled={eliminando}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {eliminando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mover a papelera"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmar: restaurar */}
      <AlertDialog open={aRestaurar !== null} onOpenChange={(open) => !open && setARestaurar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restaurar área</AlertDialogTitle>
            <AlertDialogDescription>
              {aRestaurar ? (
                <>
                  ¿Restaurar el área <strong>{aRestaurar.nombre}</strong>? Vuelve al listado con las
                  asignaciones que tenía antes de pasar a la papelera.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restaurando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                restaurar();
              }}
              disabled={restaurando}
            >
              {restaurando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Restaurar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmar: purgar (typed-confirm) */}
      <AlertDialog
        open={aPurgar !== null}
        onOpenChange={(open) => {
          if (!open) {
            setAPurgar(null);
            setConfirmPurgarTexto("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">
              Eliminar permanentemente
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {aPurgar ? (
                  <div className="space-y-3">
                    <p>
                      Estás por eliminar definitivamente el área <strong>{aPurgar.nombre}</strong>.
                      Esta acción es <strong>irreversible</strong>: se borran sus asignaciones a
                      trabajadores, cargos, contratos y equipos, y su identificador queda libre para
                      volver a usarse.
                    </p>
                    <p className="font-medium">
                      Escribe <span className="fc-num text-destructive">ELIMINAR</span> para
                      confirmar:
                    </p>
                    <Input
                      value={confirmPurgarTexto}
                      onChange={(e) => setConfirmPurgarTexto(e.target.value)}
                      placeholder="ELIMINAR"
                      className="fc-num"
                      disabled={purgando}
                      autoFocus
                    />
                  </div>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={purgando}
              onClick={() => {
                setAPurgar(null);
                setConfirmPurgarTexto("");
              }}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                purgar();
              }}
              disabled={purgando || confirmPurgarTexto !== "ELIMINAR"}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {purgando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Eliminar permanentemente"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Alta y edición ───────────────────────────────────────────────────────────

interface AreaFormDrawerProps {
  mode: "create" | "edit";
  area?: Area;
  /** Para avisar del nombre repetido sin ir al servidor. */
  activas: Area[];
  /** Ídem contra la papelera: ahí está el choque de slug que el 422 no explica. */
  papelera: Area[];
  onSaved: () => void;
}

function AreaFormDrawer({ mode, area, activas, papelera, onSaved }: AreaFormDrawerProps) {
  const [nombre, setNombre] = useState(area?.nombre ?? "");
  const [descripcion, setDescripcion] = useState(area?.descripcion ?? "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Se enciende al primer intento de guardar con el formulario incompleto. */
  const [intentado, setIntentado] = useState(false);

  const esNuevo = mode === "create";
  const nombreLimpio = nombre.trim();

  const duplicada = useMemo(
    () =>
      !!nombreLimpio &&
      activas.some((a) => a.id !== area?.id && normalizar(a.nombre) === normalizar(nombreLimpio)),
    [activas, area?.id, nombreLimpio],
  );

  /**
   * El choque contra la papelera es el que muerde en silencio: el slug es único
   * por empresa INCLUYENDO las áreas eliminadas, así que la API responde 422 con
   * un mensaje sobre un "identificador" que este formulario ya no muestra.
   * Detectarlo acá convierte ese callejón sin salida en una instrucción concreta.
   */
  const enPapelera = useMemo(
    () =>
      nombreLimpio
        ? papelera.find((a) => normalizar(a.nombre) === normalizar(nombreLimpio))
        : undefined,
    [papelera, nombreLimpio],
  );

  const usos = useMemo(() => {
    if (!area) return [];
    const equipos = area.vehiculos_count + area.maquinarias_count;
    return [
      { icono: "group", label: "Trabajadores asignados", n: area.personas_count },
      { icono: "badge", label: "Cargos que la declaran", n: area.cargos_count },
      { icono: "description", label: "Contratos que la usan", n: area.contratos_count },
      { icono: "local_shipping", label: "Vehículos y maquinaria", n: equipos },
    ].filter((u) => u.n > 0);
  }, [area]);

  const puedeGuardar = !!nombreLimpio && !duplicada && !enPapelera;

  const pista = !nombreLimpio
    ? "Escribe un nombre para continuar."
    : duplicada
      ? "Ya existe un área con ese nombre."
      : enPapelera
        ? "Hay un área con ese nombre en la papelera."
        : esNuevo
          ? "Después podrás asignarle trabajadores, cargos y equipos."
          : "Cambiar el nombre no rompe nada: todo lo asignado sigue apuntando a esta misma área.";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();

    // El botón primario NO va `disabled` cuando falta algo: quien no encuentra
    // qué le falta se queda golpeando un botón muerto justo mientras se hacía
    // esa pregunta. El click entra, enciende el aviso inline y dice qué
    // corregir. `disabled` queda para el envío en curso, donde el segundo click
    // sería un segundo POST.
    if (!puedeGuardar) {
      setIntentado(true);
      toast.error(pista);
      return;
    }

    const payload = {
      nombre: nombreLimpio,
      // El slug es un identificador interno que este formulario ya no expone.
      // En el alta se deriva del nombre; en la edición se manda TAL CUAL el que
      // ya tenía. Recalcularlo al renombrar rompería `esAreaGeneral()`, que
      // reconoce al área por defecto por `slug === "general"`.
      slug: esNuevo ? slugify(nombreLimpio) : (area?.slug ?? slugify(nombreLimpio)),
      descripcion: descripcion.trim() || null,
    };

    setIsSubmitting(true);
    try {
      if (esNuevo) {
        await api.post("/areas", payload);
        toast.success(`Área "${payload.nombre}" creada.`);
      } else if (area) {
        await api.put(`/areas/${area.id}`, payload);
        toast.success(`Cambios guardados en "${payload.nombre}".`);
      }
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && err.errors) {
        const firstError = Object.values(err.errors)[0]?.[0];
        toast.error(firstError ?? "Revisa los datos.");
      } else {
        toast.error("No se pudo guardar el área.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // El aviso inline aparece sólo después del primer intento, para no marcar en
  // rojo un campo que todavía se está escribiendo.
  const mostrarError = intentado && !puedeGuardar;

  return (
    <FormDrawerContent>
      <FormDrawerHeader>
        <div className="flex items-center gap-3">
          <span className="bg-primary-50 text-primary-hover flex size-9 shrink-0 items-center justify-center rounded-[10px]">
            <Icon name="label" size={20} />
          </span>
          <div className="min-w-0 flex-1">
            <FormDrawerTitle>{esNuevo ? "Nueva área" : "Editar área"}</FormDrawerTitle>
            <FormDrawerDescription>
              {esNuevo
                ? "Un frente de trabajo con su propia gente y sus propios equipos."
                : usos.length > 0
                  ? plural(
                      usos.reduce((acc, u) => acc + u.n, 0),
                      "registro la usa",
                      "registros la usan",
                    )
                  : "Todavía no la usa nadie."}
            </FormDrawerDescription>
          </div>
        </div>
      </FormDrawerHeader>

      <form onSubmit={submit} className={formDrawerBodyClass}>
        <div className="space-y-2" data-tour="area-nombre">
          {/* `block` en el call site: el Label del DS es flex y sin esto la
              etiqueta y el campo se acomodan en la misma línea. */}
          <Label htmlFor="nombre" className="block">
            Nombre del área
          </Label>
          <Input
            id="nombre"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Ej: Obras civiles"
            disabled={isSubmitting}
            // El nombre repetido BLOQUEA el guardado, así que lleva
            // `aria-invalid` + texto rojo y ningún borde propio: un borde ámbar
            // junto a `aria-invalid` da borde rojo con texto ámbar.
            aria-invalid={mostrarError || undefined}
            autoFocus
          />
          {mostrarError && (
            <p className="text-destructive flex items-start gap-1.5 text-xs">
              <Icon name="error" size={15} className="mt-px shrink-0" />
              <span>
                {duplicada
                  ? "Ya existe un área con ese nombre."
                  : enPapelera
                    ? "Hay un área con ese nombre en la papelera. Restáurala desde la pestaña Papelera en vez de crear una nueva."
                    : "Escribe un nombre para el área."}
              </span>
            </p>
          )}
        </div>

        <div className="space-y-2" data-tour="area-descripcion">
          <Label htmlFor="descripcion" className="block">
            Para qué se usa
          </Label>
          <Textarea
            id="descripcion"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            placeholder="Qué frente de trabajo cubre y qué incluye."
            disabled={isSubmitting}
            rows={3}
          />
          <p className="text-soft text-xs">
            Opcional, pero ayuda al resto del equipo a saber cuándo elegirla.
          </p>
        </div>

        {usos.length > 0 && (
          <div className="space-y-2">
            <span className="block text-[13px] font-semibold">Quién la está usando</span>
            <div className="space-y-1.5">
              {usos.map((u) => (
                <div key={u.icono} className="flex items-center gap-2.5 rounded-lg border px-3 py-2">
                  <Icon name={u.icono} size={18} className="text-primary-hover shrink-0" />
                  <span className="text-muted-foreground min-w-0 flex-1 text-[12.5px]">
                    {u.label}
                  </span>
                  <span className="fc-num shrink-0 text-[13px]">{u.n}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <FormDrawerFooter>
          {/* La pista dice qué falta ANTES de intentar guardar, en vez de dejar
              el botón inerte sin explicar por qué. */}
          <FormDrawerFooterHint>{pista}</FormDrawerFooterHint>
          <FormDrawerClose asChild>
            <Button type="button" variant="outline" disabled={isSubmitting}>
              Cancelar
            </Button>
          </FormDrawerClose>
          <Button
            type="submit"
            disabled={isSubmitting}
            className={cn("gap-2", !puedeGuardar && "bg-muted text-soft shadow-none hover:bg-muted")}
            data-tour="areas-guardar"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : esNuevo ? (
              <Plus className="h-4 w-4" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {esNuevo ? "Crear área" : "Guardar cambios"}
          </Button>
        </FormDrawerFooter>
      </form>
    </FormDrawerContent>
  );
}
